import { useCallback, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from '@aimsos/i18n';
import { AccordionSection, Badge, Button } from '@aimsos/ui';

import ColumnPickerModal from './ColumnPickerModal.jsx';
import InlineNote from './InlineNote.jsx';
import { PickerError, PickerSkeleton } from './PickerStates.jsx';

/** Chips shown inline before the `+{n} more` overflow. */
const VISIBLE_CHIP_COUNT = 6;

/**
 * The columns a record-set query will actually project.
 *
 * A widget's columns become query fields, and the query refuses a
 * `SYSTEM_CONTROL` one: `created_at` in the list produced
 * `DSW018 UI_QUERY_COMPONENT_FIELD_INVALID` on save, measured against dev. The
 * one exception is the entity's record uuid, and nobody picks that here — the
 * payload appends it (see `RECORD_KEY_ALIAS`).
 *
 * The field mapper carries the system ones on purpose and says so: they are
 * offered to **filters**, which narrow rows rather than project them. Only the
 * projection has to refuse them.
 */
const projectable = (column) => !column.isSystem;

/**
 * `COLUMNS TO EXPOSE` (CT-5194 / CT-5195).
 *
 * `Edit columns` opens the full picker (search, type filters, select all), but
 * a chip also carries its own dismiss `x` — `Badge`'s built-in `closable` —
 * so dropping one column does not require reopening the modal for it.
 *
 * ARP-938's `ColumnChip` hover card is deliberately absent; it is not in the V1
 * prototype and the four attributes live inside the picker instead.
 */
function ExposedColumnsSection({
  selectedFieldUuids,
  columns,
  unsupportedColumns,
  columnsByFieldUuid,
  columnsLoading,
  columnsError,
  onRetryColumns,
  onChange,
}) {
  const { t } = useTranslation('dashboards');
  const [isPickerOpen, setIsPickerOpen] = useState(false);

  const pickableColumns = useMemo(() => columns.filter(projectable), [columns]);

  const handleOpen = useCallback(() => setIsPickerOpen(true), []);
  const handleClose = useCallback(() => setIsPickerOpen(false), []);

  const handleApply = useCallback(
    (next) => {
      setIsPickerOpen(false);
      onChange(next);
    },
    [onChange],
  );

  const handleRemoveOne = useCallback(
    (fieldUuid) => onChange(selectedFieldUuids.filter((uuid) => uuid !== fieldUuid)),
    [onChange, selectedFieldUuids],
  );

  const visible = useMemo(
    () => selectedFieldUuids.slice(0, VISIBLE_CHIP_COUNT),
    [selectedFieldUuids],
  );

  const overflowCount = Math.max(0, selectedFieldUuids.length - VISIBLE_CHIP_COUNT);

  const body = () => {
    if (columnsError) {
      return (
        <PickerError
          title={t('widgetBuilder.filters.errorTitle')}
          subtitle={t('widgetBuilder.filters.errorSubtitle')}
          retryLabel={t('widgetBuilder.data.retry')}
          onRetry={onRetryColumns}
        />
      );
    }
    if (columnsLoading) return <PickerSkeleton count={2} height={28} />;

    if (selectedFieldUuids.length === 0) {
      return <InlineNote>{t('widgetBuilder.operation.noExposedColumns')}</InlineNote>;
    }

    return (
      <div className="flex min-w-0 flex-wrap items-center gap-1.5">
        {visible.map((fieldUuid) => {
          const columnLabel = columnsByFieldUuid[fieldUuid]?.displayName
            ?? columnsByFieldUuid[fieldUuid]?.internalName
            ?? fieldUuid;
          return (
            <Badge
              key={fieldUuid}
              id={fieldUuid}
              size="small"
              variant="secondary"
              closable
              onClose={handleRemoveOne}
              closeButtonAriaLabel={t('widgetBuilder.operation.removeColumnAria', { column: columnLabel })}
            >
              {columnLabel}
            </Badge>
          );
        })}
        {overflowCount > 0 ? (
          <Badge size="small" variant="neutral">
            {t('widgetBuilder.operation.moreColumns', { count: overflowCount })}
          </Badge>
        ) : null}
      </div>
    );
  };

  return (
    <>
      <AccordionSection
        title={t('widgetBuilder.operation.exposedColumns')}
        titleSize="xs"
        defaultOpen
      >
        <div className="flex flex-col gap-3 pb-4 pt-3">
          {body()}
          <div>
            <Button variant="secondary" size="sm" onClick={handleOpen} disabled={columnsLoading}>
              {selectedFieldUuids.length === 0
                ? t('widgetBuilder.operation.chooseColumns')
                : t('widgetBuilder.operation.editColumns')}
            </Button>
          </div>
        </div>
      </AccordionSection>

      <ColumnPickerModal
        isOpen={isPickerOpen}
        columns={pickableColumns}
        unsupportedColumns={unsupportedColumns}
        committedSelection={selectedFieldUuids}
        isLoading={columnsLoading}
        error={columnsError}
        onRetry={onRetryColumns}
        onClose={handleClose}
        onApply={handleApply}
      />
    </>
  );
}

const columnShape = PropTypes.shape({
  fieldUuid: PropTypes.string.isRequired,
  internalName: PropTypes.string.isRequired,
  displayName: PropTypes.string,
  description: PropTypes.string,
  type: PropTypes.string,
});

ExposedColumnsSection.defaultProps = {
  unsupportedColumns: [],
};

ExposedColumnsSection.propTypes = {
  selectedFieldUuids: PropTypes.arrayOf(PropTypes.string).isRequired,
  columns: PropTypes.arrayOf(columnShape).isRequired,
  /** Mapped fields the backend cannot take yet (CT-5480): listed in the picker, disabled. */
  unsupportedColumns: PropTypes.arrayOf(columnShape),
  columnsByFieldUuid: PropTypes.objectOf(columnShape).isRequired,
  columnsLoading: PropTypes.bool,
  columnsError: PropTypes.object,
  onRetryColumns: PropTypes.func.isRequired,
  onChange: PropTypes.func.isRequired,
};

export default ExposedColumnsSection;
